import { Link } from 'react-router-dom';
import type { FocusTask } from '../../lib/focus';
import type { RowPatch } from '../../lib/tasks';
import { formatDuration } from '../../lib/duration';
import { isOverdue } from '../../lib/taskDates';
import { CHECKBOX_BOX, FOCUS_RING_FLUSH } from '../ui';
import { PlayIcon, StopIcon, UndoIcon } from '../icons';
import { FOCUS_GRID } from './focusGrid';

/**
 * One task in the current sitting.
 *
 * A third sibling of TaskRow and DayTaskRow, and it copies the same two rules
 * they agree on: the title `<Link>` fills the row via `after:absolute
 * after:inset-0`, and anything else clickable sits at `relative z-10` so its own
 * click does not also follow that link. Like both of them, it owns no request --
 * it reports intent, and the hook holds the mutation.
 *
 * Where it parts company with DayTaskRow is what a row is *for*. A day row is
 * triage: its controls walk a task along its statuses, because you are deciding
 * what to do with it. By the time a task is here that decision is made, so the
 * controls are the three things you actually do while working -- tick it off,
 * time it, or admit you are not getting to it.
 *
 * A finished row stays, struck through, rather than dropping out -- it sinks to
 * the foot of the list instead, so what is left to do stays under your eye. On
 * the day card a completed task leaves because it no longer belongs; here it is
 * the score. The sinking is the card's doing, not the row's: see
 * `inWorkingOrder` in FocusCard.
 */
export function FocusTaskRow({
  task,
  active,
  running,
  busy,
  onPatch,
  onToggleTimer,
  onRemove,
}: {
  task: FocusTask;
  /** The task this row is for is open in the panel. */
  active: boolean;
  /** The caller's one timer is on this task. */
  running: boolean;
  /** A *timer* request for this row is in flight, so its controls are inert:
   *  picking and ticking off are optimistic, but the clock is one running entry
   *  per person and a second click before the first lands would move it twice. */
  busy: boolean;
  onPatch: (patch: RowPatch) => void;
  onToggleTimer: () => void;
  onRemove: () => void;
}) {
  const done = task.status === 'DONE';
  // Still worth saying on a task you have picked: a session is usually the thing
  // that clears a backlog, so several of these rows are routinely late.
  const overdue = !done && isOverdue(task.dueDate);

  return (
    <li
      className={`${FOCUS_GRID} group relative px-4 py-2 transition ${
        active ? 'bg-honey-50 ring-1 ring-inset ring-honey-300' : 'hover:bg-stone-50'
      }`}
    >
      {/*
        A button with `role="checkbox"`, not an `<input>` -- the same choice
        TaskRow makes, for the same reason: the hit area has to grow past the
        drawn box without the box itself growing, which `after:-inset-1.5` does
        and a native control's own box does not.

        Ticking it toggles against To do rather than restoring whatever status it
        held before. A session is not where you manage a workflow: the two
        answers this control has are "done" and "not done yet", and anything more
        precise is one click away in the panel.
      */}
      <button
        type="button"
        role="checkbox"
        aria-checked={done}
        disabled={busy}
        onClick={() => onPatch({ status: done ? 'TODO' : 'DONE' })}
        title={done ? 'Not done after all' : 'Mark done'}
        aria-label={`${done ? 'Reopen' : 'Mark done'}: ${labelFor(task)}`}
        className={`z-10 ${CHECKBOX_BOX} disabled:cursor-not-allowed disabled:opacity-50 ${
          done
            ? 'border-emerald-500 bg-emerald-500 text-white'
            : 'border-stone-300 text-transparent hover:border-emerald-500 hover:text-emerald-500'
        }`}
      >
        {/* Drawn rather than a CheckIcon: at 0.5rem inside a 1rem ring the
            shared glyph's 1.5 stroke reads as a smudge. */}
        <svg viewBox="0 0 10 10" className="h-2 w-2" aria-hidden="true" focusable="false">
          <path
            d="m1.5 5 2.5 2.5 4.5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {/* `min-w-0` because the grid track is `minmax(0,1fr)`: without it this
          flex row refuses to shrink and pushes the columns beside it off the
          card. Same reason DayTaskRow's title cell has it. */}
      <span className="flex min-w-0 items-center gap-1">
        {task.parent ? (
          <>
            <span className="max-w-[45%] shrink-0 truncate text-sm text-stone-500">
              {task.parent.title}
            </span>
            <span aria-hidden="true" className="shrink-0 text-sm text-stone-400">
              &rsaquo;
            </span>
          </>
        ) : null}

        <Link
          to={`/task/${task.id}`}
          title={task.parent ? `${task.title} — subtask of ${task.parent.title}` : task.title}
          className={`truncate text-sm after:absolute after:inset-0 ${
            done ? 'text-stone-400 line-through' : 'text-stone-900'
          }`}
        >
          {task.title}
        </Link>
      </span>

      {/* Its own link, so the project name is a way into the project and not
          just a caption -- hence the z-10 to win against the row link above. */}
      <Link
        to={`/projects/${task.project.id}/tasks`}
        title={task.project.name}
        className="relative z-10 hidden truncate text-xs text-stone-500 hover:text-stone-900 hover:underline sm:block"
      >
        {task.project.name}
      </Link>

      {/*
        Tracked time, or the due date when nothing has been logged.

        One track doing two jobs, because they are never both the answer you
        want. Before you start, "when is this due" is the live question -- and it
        is why the task is in the session. Once the clock has run on it, the
        deadline is a decided matter and the hours are what you are watching.

        The total is the task's, not the session's: `trackedSeconds` is what the
        list already carries, and re-querying every row for its share of this
        sitting would be a query per row for a number nobody is auditing.
      */}
      <span
        className={`text-xs tabular-nums ${
          running
            ? 'font-medium text-honey-700'
            : overdue && task.trackedSeconds === 0
              ? 'font-medium text-red-600'
              : 'text-stone-500'
        }`}
        title={
          task.trackedSeconds > 0
            ? `${formatDuration(task.trackedSeconds)} logged in total`
            : overdue
              ? 'Overdue'
              : undefined
        }
      >
        {task.trackedSeconds > 0
          ? formatDuration(task.trackedSeconds)
          : overdue
            ? 'overdue'
            : null}
      </span>

      {/*
        `invisible`, never `opacity-0`: an `opacity-0` element still hit-tests,
        and these sit inside a row whose `<Link>` covers the whole area, so an
        invisible-but-clickable button at the trailing edge would silently eat
        clicks meant to open the task.

        A running timer's Stop button is the exception and stays visible at every
        width. Hiding the only evidence that a clock is ticking until you happen
        to hover the right row is how an hour gets logged to the wrong task.
      */}
      <span className="flex items-center justify-end gap-0.5">
        <button
          type="button"
          disabled={busy}
          onClick={onToggleTimer}
          title={running ? 'Stop the timer' : 'Start a timer'}
          aria-label={`${running ? 'Stop the timer on' : 'Start a timer on'} ${labelFor(task)}`}
          aria-pressed={running}
          className={`relative z-10 flex h-5 w-5 items-center justify-center rounded transition after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-[''] disabled:cursor-not-allowed disabled:opacity-50 ${
            running
              ? 'bg-honey-100 text-honey-700 hover:bg-honey-200'
              : 'text-stone-500 hover:bg-stone-200/70 hover:text-stone-900 sm:invisible sm:group-hover:visible sm:group-focus-within:visible'
          } ${FOCUS_RING_FLUSH}`}
        >
          {running ? <StopIcon className="h-3 w-3" /> : <PlayIcon className="h-3 w-3" />}
        </button>

        <button
          type="button"
          disabled={busy}
          onClick={onRemove}
          title="Put back on the pile"
          aria-label={`Put ${labelFor(task)} back on the pile`}
          className={`relative z-10 flex h-5 w-5 items-center justify-center rounded text-stone-500 transition after:absolute after:-inset-y-1.5 after:inset-x-0 after:content-[''] hover:bg-stone-200/70 hover:text-stone-900 disabled:cursor-not-allowed disabled:opacity-50 sm:invisible sm:group-hover:visible sm:group-focus-within:visible ${FOCUS_RING_FLUSH}`}
        >
          <UndoIcon className="h-3.5 w-3.5" />
        </button>
      </span>
    </li>
  );
}

/** The parent is part of the name for the same reason it is part of the visible
 *  row: "Mark on Mac done" is not a sentence anyone can act on. */
const labelFor = (task: FocusTask): string =>
  task.parent ? `${task.parent.title}: ${task.title}` : task.title;
